import React, { useState, useRef, useEffect } from "react";

const UI = {
  spacing: { xs: 6, sm: 8, md: 12, lg: 16, xl: 24 },
  radius:  { sm: 6, md: 8, lg: 12 },
  colors: {
    primary:   "#0F172A",
    secondary: "#334155",
    muted:     "#64748B",
    subtle:    "#94A3B8",
    border:    "#E2E8F0",
    bg:        "#F8FAFC",
    white:     "#FFFFFF"
  }
};

const paymentPills = {
  paid: { bg: "#D1FAE5", color: "#059669", text: "Paid" },
  pending: { bg: "#FEF3C7", color: "#D97706", text: "Pending" },
  refunded: { bg: "#F1F5F9", color: "#64748B", text: "Refunded" }
};

const statusPills = {
  requested: { bg: "#EEF2FF", color: "#6366F1", text: "Requested" },
  assigned: { bg: "#E0F2FE", color: "#0369A1", text: "Assigned" },
  in_progress: { bg: "#FEF3C7", color: "#D97706", text: "In Progress" },
  completed: { bg: "#D1FAE5", color: "#059669", text: "Completed" },
  cancelled: { bg: "#F1F5F9", color: "#64748B", text: "Cancelled" },
  disputed: { bg: "#FEE2E2", color: "#DC2626", text: "Disputed" }
};

const avatarBg = (name) => {
  if (!name) return "#F3F4F6";
  const colors = ["#EEF2FF", "#E0F2FE", "#D1FAE5", "#FEF3C7", "#FEE2E2", "#F3E8FF", "#FFE4E6"];
  const index = name.charCodeAt(0) % colors.length;
  return colors[index];
};

const avatarColor = (name) => {
  if (!name) return "#9CA3AF";
  const colors = ["#4F46E5", "#0369A1", "#059669", "#D97706", "#DC2626", "#9333EA", "#E11D48"];
  const index = name.charCodeAt(0) % colors.length;
  return colors[index];
};

const initials = (name) => {
  if (!name) return "?";
  return name.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase();
};

const formatDate = (dateStr) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

const SortIcon = ({ field, sortField, sortDir }) => {
  if (sortField !== field) return <span style={{ opacity: 0.3 }}>↕</span>;
  return <span>{sortDir === 1 ? "↑" : "↓"}</span>;
};

const DropdownMenu = ({ onViewDetails, onMarkComplete, onCancelJob, onOpenDispute, onRefund, canComplete, canCancel, canDispute, canRefund, onClose }) => {
  const ref = useRef(null);
  
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose]);
  
  return (
    <div ref={ref} style={{
      position: "absolute",
      top: "100%",
      right: 0,
      zIndex: 99,
      background: UI.colors.white,
      border: `1px solid ${UI.colors.border}`,
      borderRadius: UI.radius.md,
      boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
      width: 180,
      overflow: "hidden"
    }}>
      <button onClick={onViewDetails} style={{
        width: "100%",
        padding: `${UI.spacing.sm}px ${UI.spacing.md}px`,
        background: "none",
        border: "none",
        textAlign: "left",
        fontFamily: "'Plus Jakarta Sans', sans-serif",
        fontSize: 12,
        color: UI.colors.primary,
        cursor: "pointer"
      }} onMouseEnter={(e) => e.currentTarget.style.background = UI.colors.bg}
         onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}>
        View Details
      </button>
      <button onClick={onMarkComplete} disabled={!canComplete} style={{
        width: "100%",
        padding: `${UI.spacing.sm}px ${UI.spacing.md}px`,
        background: "none",
        border: "none",
        textAlign: "left",
        fontFamily: "'Plus Jakarta Sans', sans-serif",
        fontSize: 12,
        color: canComplete ? UI.colors.primary : UI.colors.subtle,
        cursor: canComplete ? "pointer" : "not-allowed",
        opacity: canComplete ? 1 : 0.5
      }} onMouseEnter={(e) => { if (canComplete) e.currentTarget.style.background = UI.colors.bg; }}
         onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}>
        Mark as Completed
      </button>
      <button onClick={onCancelJob} disabled={!canCancel} style={{
        width: "100%",
        padding: `${UI.spacing.sm}px ${UI.spacing.md}px`,
        background: "none",
        border: "none",
        textAlign: "left",
        fontFamily: "'Plus Jakarta Sans', sans-serif",
        fontSize: 12,
        color: canCancel ? "#DC2626" : UI.colors.subtle,
        cursor: canCancel ? "pointer" : "not-allowed",
        opacity: canCancel ? 1 : 0.5
      }} onMouseEnter={(e) => { if (canCancel) e.currentTarget.style.background = UI.colors.bg; }}
         onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}>
        Cancel Job
      </button>
      <button onClick={onOpenDispute} disabled={!canDispute} style={{
        width: "100%",
        padding: `${UI.spacing.sm}px ${UI.spacing.md}px`,
        background: "none",
        border: "none",
        textAlign: "left",
        fontFamily: "'Plus Jakarta Sans', sans-serif",
        fontSize: 12,
        color: canDispute ? UI.colors.primary : UI.colors.subtle,
        cursor: canDispute ? "pointer" : "not-allowed",
        opacity: canDispute ? 1 : 0.5
      }} onMouseEnter={(e) => { if (canDispute) e.currentTarget.style.background = UI.colors.bg; }}
         onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}>
        Open Dispute
      </button>
      {canRefund && (
        <button onClick={onRefund} style={{
          width: "100%",
          padding: `${UI.spacing.sm}px ${UI.spacing.md}px`,
          background: "none",
          border: "none",
          textAlign: "left",
          fontFamily: "'Plus Jakarta Sans', sans-serif",
          fontSize: 12,
          color: "#DC2626",
          cursor: "pointer"
        }} onMouseEnter={(e) => e.currentTarget.style.background = UI.colors.bg}
           onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}>
          Refund
        </button>
      )}
    </div>
  );
};

const JobsTable = ({
  jobs,
  allJobs,
  selectedIds,
  onRowClick,
  onSelect,
  onSelectAll,
  onSort,
  sortField,
  sortDir,
  currentPage,
  onPageChange,
  pageSize,
  totalPages,
  loading,
  onClearFilters,
  onMarkComplete,
  onCancelJob,
  onOpenDispute,
  onRefundJob,
  onCustomerClick,
  onProClick
}) => {
  const [openDropdownId, setOpenDropdownId] = useState(null);
  
  const startIndex = (currentPage - 1) * pageSize + 1;
  const endIndex = Math.min(currentPage * pageSize, jobs.length);
  const allSelected = jobs.length > 0 && jobs.every(job => selectedIds.has(job.id));
  const someSelected = jobs.some(job => selectedIds.has(job.id));
  
  const handleSelectAll = (e) => {
    onSelectAll(e.target.checked, jobs.map(j => j.id));
  };
  
  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push('...');
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);
      for (let i = start; i <= end; i++) pages.push(i);
      if (currentPage < totalPages - 2) pages.push('...');
      pages.push(totalPages);
    }
    return pages;
  };
  
  if (loading) {
    return (
      <div style={{
        background: UI.colors.white,
        border: `1px solid ${UI.colors.border}`,
        borderRadius: UI.radius.lg,
        overflow: "hidden"
      }}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr><th style={{ width: 44, padding: UI.spacing.md }}><div style={{ width: 16, height: 16, background: "#E2E8F0", borderRadius: 3 }} /></th>
                <th style={{ width: 120, padding: UI.spacing.md }}><div style={{ height: 20, background: "#E2E8F0", borderRadius: 4 }} /></th>
                <th style={{ width: 200, padding: UI.spacing.md }}><div style={{ height: 20, background: "#E2E8F0", borderRadius: 4 }} /></th>
                <th style={{ width: 200, padding: UI.spacing.md }}><div style={{ height: 20, background: "#E2E8F0", borderRadius: 4 }} /></th>
                <th style={{ width: 120, padding: UI.spacing.md }}><div style={{ height: 20, background: "#E2E8F0", borderRadius: 4 }} /></th>
                <th style={{ width: 130, padding: UI.spacing.md }}><div style={{ height: 20, background: "#E2E8F0", borderRadius: 4 }} /></th>
                <th style={{ width: 110, padding: UI.spacing.md }}><div style={{ height: 20, background: "#E2E8F0", borderRadius: 4 }} /></th>
                <th style={{ width: 110, padding: UI.spacing.md }}><div style={{ height: 20, background: "#E2E8F0", borderRadius: 4 }} /></th>
                <th style={{ width: 120, padding: UI.spacing.md }}><div style={{ height: 20, background: "#E2E8F0", borderRadius: 4 }} /></th>
                <th style={{ width: 130, padding: UI.spacing.md }}><div style={{ height: 20, background: "#E2E8F0", borderRadius: 4 }} /></th>
                <th style={{ width: 80, padding: UI.spacing.md }}><div style={{ height: 20, background: "#E2E8F0", borderRadius: 4 }} /></th>
              </tr>
            </thead>
            <tbody>
              {[...Array(10)].map((_, i) => (
                <tr key={i}>
                  {[...Array(11)].map((_, j) => (
                    <td key={j} style={{ padding: UI.spacing.md }}>
                      <div style={{
                        height: 20,
                        background: "linear-gradient(90deg, #F1F5F9 25%, #E2E8F0 50%, #F1F5F9 75%)",
                        backgroundSize: "200% 100%",
                        animation: "shimmer 1.4s infinite",
                        borderRadius: 4
                      }} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }
  
  if (jobs.length === 0) {
    return (
      <div style={{
        background: UI.colors.white,
        border: `1px solid ${UI.colors.border}`,
        borderRadius: UI.radius.lg,
        padding: UI.spacing.xl,
        textAlign: "center"
      }}>
        <div style={{ fontSize: 48, marginBottom: UI.spacing.md }}>📋</div>
        <div style={{
          fontFamily: "'Plus Jakarta Sans', sans-serif",
          fontSize: 14,
          fontWeight: 600,
          color: UI.colors.primary,
          marginBottom: UI.spacing.sm
        }}>No jobs found</div>
        <div style={{
          fontFamily: "'Plus Jakarta Sans', sans-serif",
          fontSize: 12,
          color: UI.colors.muted,
          marginBottom: UI.spacing.md
        }}>Try adjusting your filters</div>
        <button onClick={onClearFilters} style={{
          padding: `${UI.spacing.sm}px ${UI.spacing.lg}px`,
          background: UI.colors.primary,
          color: UI.colors.white,
          border: "none",
          borderRadius: UI.radius.sm,
          fontFamily: "'Plus Jakarta Sans', sans-serif",
          fontSize: 12,
          fontWeight: 600,
          cursor: "pointer"
        }}>Clear filters</button>
      </div>
    );
  }
  
  return (
    <div>
      <div style={{
        background: UI.colors.white,
        border: `1px solid ${UI.colors.border}`,
        borderRadius: UI.radius.lg,
        overflow: "hidden"
      }}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead style={{ background: UI.colors.bg, borderBottom: `1px solid ${UI.colors.border}` }}>
              <tr>
                <th style={{ width: 44, padding: UI.spacing.md, textAlign: "center" }}>
                  <input
                    type="checkbox"
                    checked={allSelected}
                    ref={(input) => {
                      if (input) input.indeterminate = someSelected && !allSelected;
                    }}
                    onChange={handleSelectAll}
                    style={{ cursor: "pointer" }}
                  />
                </th>
                <th style={{ width: 120, padding: UI.spacing.md, textAlign: "left" }}>
                  <button onClick={() => onSort('jobId')} style={{
                    background: "none",
                    border: "none",
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    fontSize: 12,
                    fontWeight: 600,
                    color: UI.colors.secondary,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 4
                  }}>Job ID <SortIcon field="jobId" sortField={sortField} sortDir={sortDir} /></button>
                </th>
                <th style={{ width: 200, padding: UI.spacing.md, textAlign: "left" }}>
                  <span style={{
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    fontSize: 12,
                    fontWeight: 600,
                    color: UI.colors.secondary
                  }}>Customer</span>
                </th>
                <th style={{ width: 200, padding: UI.spacing.md, textAlign: "left" }}>
                  <span style={{
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    fontSize: 12,
                    fontWeight: 600,
                    color: UI.colors.secondary
                  }}>Pro</span>
                </th>
                <th style={{ width: 120, padding: UI.spacing.md, textAlign: "left" }}>
                  <span style={{
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    fontSize: 12,
                    fontWeight: 600,
                    color: UI.colors.secondary
                  }}>Category</span>
                </th>
                <th style={{ width: 130, padding: UI.spacing.md, textAlign: "left" }}>
                  <span style={{
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    fontSize: 12,
                    fontWeight: 600,
                    color: UI.colors.secondary
                  }}>Location</span>
                </th>
                <th style={{ width: 110, padding: UI.spacing.md, textAlign: "left" }}>
                  <button onClick={() => onSort('date')} style={{
                    background: "none",
                    border: "none",
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    fontSize: 12,
                    fontWeight: 600,
                    color: UI.colors.secondary,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 4
                  }}>Date <SortIcon field="date" sortField={sortField} sortDir={sortDir} /></button>
                </th>
                <th style={{ width: 110, padding: UI.spacing.md, textAlign: "right" }}>
                  <button onClick={() => onSort('price')} style={{
                    background: "none",
                    border: "none",
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    fontSize: 12,
                    fontWeight: 600,
                    color: UI.colors.secondary,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    gap: 4,
                    width: "100%"
                  }}>Price <SortIcon field="price" sortField={sortField} sortDir={sortDir} /></button>
                </th>
                <th style={{ width: 120, padding: UI.spacing.md, textAlign: "left" }}>
                  <button onClick={() => onSort('paymentStatus')} style={{
                    background: "none",
                    border: "none",
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    fontSize: 12,
                    fontWeight: 600,
                    color: UI.colors.secondary,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 4
                  }}>Payment <SortIcon field="paymentStatus" sortField={sortField} sortDir={sortDir} /></button>
                </th>
                <th style={{ width: 130, padding: UI.spacing.md, textAlign: "left" }}>
                  <button onClick={() => onSort('status')} style={{
                    background: "none",
                    border: "none",
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    fontSize: 12,
                    fontWeight: 600,
                    color: UI.colors.secondary,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 4
                  }}>Status <SortIcon field="status" sortField={sortField} sortDir={sortDir} /></button>
                </th>
                <th style={{ width: 80, padding: UI.spacing.md, textAlign: "center" }}>
                  <span style={{
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    fontSize: 12,
                    fontWeight: 600,
                    color: UI.colors.secondary
                  }}>Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {jobs.map((job) => {
                const isSelected = selectedIds.has(job.id);
                const isAtRiskRow = (job.status === "disputed") || (job.status === "completed" && job.paymentStatus === "pending") || (job.status === "cancelled" && job.timeline && job.timeline.length > 1);
                
                return (
                  <tr
                    key={job.id}
                    onClick={() => onRowClick(job)}
                    style={{
                      background: isSelected ? "#F0FDF4" : (isAtRiskRow ? "#FFF5F5" : UI.colors.white),
                      borderBottom: `1px solid ${UI.colors.border}`,
                      cursor: "pointer",
                      transition: "background 0.15s ease"
                    }}
                    onMouseEnter={(e) => {
                      if (!isSelected) e.currentTarget.style.background = UI.colors.bg;
                    }}
                    onMouseLeave={(e) => {
                      if (!isSelected) e.currentTarget.style.background = isAtRiskRow ? "#FFF5F5" : UI.colors.white;
                    }}
                  >
                    <td style={{ padding: UI.spacing.md, textAlign: "center" }} onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => onSelect(job.id)}
                        style={{ cursor: "pointer" }}
                      />
                    </td>
                    <td style={{ padding: UI.spacing.md, fontFamily: "monospace", fontSize: 12, fontWeight: 600, color: UI.colors.primary }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        {isAtRiskRow && <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#EF4444" }} />}
                        <span>{job.jobId}</span>
                      </div>
                    </td>
                    <td style={{ padding: UI.spacing.md }} onClick={(e) => e.stopPropagation()}>
                      <div style={{ display: "flex", alignItems: "center", gap: UI.spacing.sm, cursor: "pointer" }} onClick={() => onCustomerClick(job)}>
                        <div style={{
                          width: 32,
                          height: 32,
                          borderRadius: "50%",
                          background: avatarBg(job.customer.name),
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontWeight: 600,
                          fontSize: 12,
                          fontFamily: "'Plus Jakarta Sans', sans-serif",
                          color: avatarColor(job.customer.name)
                        }}>
                          {initials(job.customer.name)}
                        </div>
                        <div style={{ minWidth: 0 }}>
                          <div style={{
                            fontFamily: "'Plus Jakarta Sans', sans-serif",
                            fontSize: 12,
                            fontWeight: 600,
                            color: UI.colors.primary,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap"
                          }}>{job.customer.name}</div>
                          <div style={{
                            fontFamily: "'Plus Jakarta Sans', sans-serif",
                            fontSize: 10,
                            color: UI.colors.muted
                          }}>{job.customer.phone}</div>
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: UI.spacing.md }} onClick={(e) => e.stopPropagation()}>
                      {job.pro ? (
                        <div style={{ display: "flex", alignItems: "center", gap: UI.spacing.sm, cursor: "pointer" }} onClick={() => onProClick(job)}>
                          <div style={{
                            width: 32,
                            height: 32,
                            borderRadius: "50%",
                            background: avatarBg(job.pro.name),
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontWeight: 600,
                            fontSize: 12,
                            fontFamily: "'Plus Jakarta Sans', sans-serif",
                            color: avatarColor(job.pro.name)
                          }}>
                            {initials(job.pro.name)}
                          </div>
                          <div style={{ minWidth: 0 }}>
                            <div style={{
                              fontFamily: "'Plus Jakarta Sans', sans-serif",
                              fontSize: 12,
                              fontWeight: 600,
                              color: UI.colors.primary,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap"
                            }}>{job.pro.name}</div>
                            <div style={{
                              fontFamily: "'Plus Jakarta Sans', sans-serif",
                              fontSize: 10,
                              color: UI.colors.muted
                            }}>{job.pro.phone}</div>
                          </div>
                        </div>
                      ) : (
                        <div style={{
                          fontFamily: "'Plus Jakarta Sans', sans-serif",
                          fontSize: 11,
                          color: UI.colors.subtle,
                          fontStyle: "italic"
                        }}>Unassigned</div>
                      )}
                    </td>
                    <td style={{ padding: UI.spacing.md }}>
                      <span style={{
                        fontFamily: "'Plus Jakarta Sans', sans-serif",
                        fontSize: 12,
                        color: UI.colors.secondary
                      }}>{job.category}</span>
                    </td>
                    <td style={{ padding: UI.spacing.md }}>
                      <span style={{
                        fontFamily: "'Plus Jakarta Sans', sans-serif",
                        fontSize: 12,
                        color: UI.colors.secondary
                      }}>{job.location.split(",")[0].trim()}</span>
                    </td>
                    <td style={{ padding: UI.spacing.md }}>
                      <span style={{
                        fontFamily: "'Plus Jakarta Sans', sans-serif",
                        fontSize: 12,
                        color: UI.colors.secondary
                      }}>{formatDate(job.date)}</span>
                    </td>
                    <td style={{ padding: UI.spacing.md, textAlign: "right" }}>
                      <span style={{
                        fontFamily: "'Plus Jakarta Sans', sans-serif",
                        fontSize: 12,
                        fontWeight: 600,
                        color: UI.colors.primary
                      }}>KSh {job.price.toLocaleString()}</span>
                    </td>
                    <td style={{ padding: UI.spacing.md }}>
                      <span style={{
                        background: paymentPills[job.paymentStatus]?.bg || UI.colors.bg,
                        color: paymentPills[job.paymentStatus]?.color || UI.colors.muted,
                        padding: "3px 10px",
                        borderRadius: 20,
                        fontSize: 11,
                        fontWeight: 700,
                        fontFamily: "'Plus Jakarta Sans', sans-serif",
                        display: "inline-block"
                      }}>
                        {paymentPills[job.paymentStatus]?.text || job.paymentStatus}
                      </span>
                    </td>
                    <td style={{ padding: UI.spacing.md }}>
                      <span style={{
                        background: statusPills[job.status]?.bg || UI.colors.bg,
                        color: statusPills[job.status]?.color || UI.colors.muted,
                        padding: "3px 10px",
                        borderRadius: 20,
                        fontSize: 11,
                        fontWeight: 700,
                        fontFamily: "'Plus Jakarta Sans', sans-serif",
                        display: "inline-block"
                      }}>
                        {statusPills[job.status]?.text || job.status}
                      </span>
                    </td>
                    <td style={{ padding: UI.spacing.md, textAlign: "center", position: "relative" }} onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => setOpenDropdownId(openDropdownId === job.id ? null : job.id)}
                        style={{
                          background: "none",
                          border: "none",
                          fontSize: 18,
                          cursor: "pointer",
                          color: UI.colors.muted
                        }}
                      >
                        ⋯
                      </button>
                      {openDropdownId === job.id && (
                        <DropdownMenu
                          onViewDetails={() => {
                            onRowClick(job);
                            setOpenDropdownId(null);
                          }}
                          onMarkComplete={() => {
                            onMarkComplete(job.id);
                            setOpenDropdownId(null);
                          }}
                          onCancelJob={() => {
                            if (window.confirm("Cancel this job?")) onCancelJob(job.id);
                            setOpenDropdownId(null);
                          }}
                          onOpenDispute={() => {
                            onOpenDispute(job.id);
                            setOpenDropdownId(null);
                          }}
                          onRefund={() => {
                            if (window.confirm("Process refund for this job?")) onRefundJob(job.id);
                            setOpenDropdownId(null);
                          }}
                          canComplete={job.status !== "completed"}
                          canCancel={job.status !== "completed" && job.status !== "cancelled"}
                          canDispute={job.status !== "disputed"}
                          canRefund={job.paymentStatus === "paid"}
                          onClose={() => setOpenDropdownId(null)}
                        />
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      
      {totalPages > 1 && (
        <div style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginTop: UI.spacing.lg
        }}>
          <div style={{
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            fontSize: 12,
            color: UI.colors.muted
          }}>
            Showing {startIndex}–{endIndex} of {allJobs.length}
          </div>
          <div style={{ display: "flex", gap: UI.spacing.sm }}>
            <button
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage === 1}
              style={{
                padding: `${UI.spacing.sm}px ${UI.spacing.md}px`,
                background: UI.colors.white,
                border: `1px solid ${UI.colors.border}`,
                borderRadius: UI.radius.sm,
                cursor: currentPage === 1 ? "not-allowed" : "pointer",
                opacity: currentPage === 1 ? 0.5 : 1,
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontSize: 12
              }}
            >
              ← Prev
            </button>
            {getPageNumbers().map((page, idx) => (
              <button
                key={idx}
                onClick={() => typeof page === 'number' && onPageChange(page)}
                style={{
                  padding: `${UI.spacing.sm}px ${UI.spacing.md}px`,
                  background: currentPage === page ? UI.colors.primary : UI.colors.white,
                  color: currentPage === page ? UI.colors.white : UI.colors.primary,
                  border: `1px solid ${UI.colors.border}`,
                  borderRadius: UI.radius.sm,
                  cursor: typeof page === 'number' ? "pointer" : "default",
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  fontSize: 12,
                  fontWeight: currentPage === page ? 600 : 400
                }}
              >
                {page}
              </button>
            ))}
            <button
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              style={{
                padding: `${UI.spacing.sm}px ${UI.spacing.md}px`,
                background: UI.colors.white,
                border: `1px solid ${UI.colors.border}`,
                borderRadius: UI.radius.sm,
                cursor: currentPage === totalPages ? "not-allowed" : "pointer",
                opacity: currentPage === totalPages ? 0.5 : 1,
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontSize: 12
              }}
            >
              Next →
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobsTable;